/**
 * Exploitatie - Exploitatierekening (Winst & Verlies)
 * EXACT gekopieerd van Morris templates/exploitatierekening.html
 */

import { useState, useEffect } from 'react';
import BoekhoudingLayout from '../../components/boekhouding/BoekhoudingLayout';
import styles from '../../styles/Boekhouding.module.css';
import { boekhoudingService, Exploitatie as ExploitatieData } from '../../services/boekhouding.service';

const Exploitatie = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<ExploitatieData | null>(null);

  // Huidig boekjaar uit localStorage
  const huidigJaar = parseInt(localStorage.getItem('boekhouding_jaar') || new Date().getFullYear().toString());

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const result = await boekhoudingService.getExploitatie(huidigJaar);
        setData(result);
      } catch (err) {
        console.error('Error loading exploitatie:', err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [huidigJaar]);

  const formatBedrag = (bedrag: number | undefined): string => {
    if (bedrag === undefined || bedrag === null) return '';
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR',
    }).format(Number(bedrag) || 0);
  };

  if (loading) {
    return (
      <BoekhoudingLayout title={`Exploitatierekening ${huidigJaar}`}>
        <div className={styles.loading}>Laden...</div>
      </BoekhoudingLayout>
    );
  }

  if (!data) {
    return (
      <BoekhoudingLayout title={`Exploitatierekening ${huidigJaar}`}>
        <div className={styles.emptyState}>Geen data gevonden</div>
      </BoekhoudingLayout>
    );
  }

  // Bereken kosten totalen per kwartaal - EXACT zoals Morris Jinja namespace
  const kostenQ1 = data.kosten.reduce((sum, k) => sum + (k.kwartalen[0] || 0), 0);
  const kostenQ2 = data.kosten.reduce((sum, k) => sum + (k.kwartalen[1] || 0), 0);
  const kostenQ3 = data.kosten.reduce((sum, k) => sum + (k.kwartalen[2] || 0), 0);
  const kostenQ4 = data.kosten.reduce((sum, k) => sum + (k.kwartalen[3] || 0), 0);

  return (
    <BoekhoudingLayout title={`Exploitatierekening ${huidigJaar}`}>
      {/* EXACT Morris exploitatierekening.html structuur */}
      <div className={styles.explContainer}>
        <div className={styles.card}>
          <table className={styles.explTable}>
            <colgroup>
              <col /><col /><col /><col /><col /><col />
            </colgroup>
            <thead>
              <tr>
                <th></th>
                <th className={styles.alignRight}>Totaal</th>
                <th className={styles.alignRight}>Q1</th>
                <th className={styles.alignRight}>Q2</th>
                <th className={styles.alignRight}>Q3</th>
                <th className={styles.alignRight}>Q4</th>
              </tr>
            </thead>
            <tbody>
              {/* Omzet - Morris: omzet-row */}
              <tr className={styles.omzetRow}>
                <td><strong>Verkoop</strong></td>
                <td className={styles.alignRight}>
                  <strong>{formatBedrag(data.omzet.kwartalen[0] + data.omzet.kwartalen[1] + data.omzet.kwartalen[2] + data.omzet.kwartalen[3])}</strong>
                </td>
                <td className={styles.alignRight}>{formatBedrag(data.omzet.kwartalen[0])}</td>
                <td className={styles.alignRight}>{formatBedrag(data.omzet.kwartalen[1])}</td>
                <td className={styles.alignRight}>{formatBedrag(data.omzet.kwartalen[2])}</td>
                <td className={styles.alignRight}>{formatBedrag(data.omzet.kwartalen[3])}</td>
              </tr>

              {/* Kosten - Morris: kosten / kosten-light */}
              {data.kosten.map((kost, index) => (
                <tr key={index}>
                  <td>{kost.naam}</td>
                  <td className={`${styles.alignRight} ${styles.kosten}`}>
                    {formatBedrag((kost.kwartalen[0] || 0) + (kost.kwartalen[1] || 0) + (kost.kwartalen[2] || 0) + (kost.kwartalen[3] || 0))}
                  </td>
                  <td className={`${styles.alignRight} ${styles.kostenLight}`}>
                    {kost.kwartalen[0] ? formatBedrag(kost.kwartalen[0]) : ''}
                  </td>
                  <td className={`${styles.alignRight} ${styles.kostenLight}`}>
                    {kost.kwartalen[1] ? formatBedrag(kost.kwartalen[1]) : ''}
                  </td>
                  <td className={`${styles.alignRight} ${styles.kostenLight}`}>
                    {kost.kwartalen[2] ? formatBedrag(kost.kwartalen[2]) : ''}
                  </td>
                  <td className={`${styles.alignRight} ${styles.kostenLight}`}>
                    {kost.kwartalen[3] ? formatBedrag(kost.kwartalen[3]) : ''}
                  </td>
                </tr>
              ))}

              {/* Separator - Morris: separator-row */}
              <tr className={styles.separatorRow}>
                <td></td>
                <td className={styles.alignRight}>========</td>
                <td colSpan={4}></td>
              </tr>

              {/* Totaal kosten - Morris: kosten-totaal-row */}
              <tr className={styles.kostenTotaalRow}>
                <td>Totaal kosten</td>
                <td className={`${styles.alignRight} ${styles.kosten}`}>
                  <strong>{formatBedrag(kostenQ1 + kostenQ2 + kostenQ3 + kostenQ4)}</strong>
                </td>
                <td className={`${styles.alignRight} ${styles.kostenLight}`}>{formatBedrag(kostenQ1)}</td>
                <td className={`${styles.alignRight} ${styles.kostenLight}`}>{formatBedrag(kostenQ2)}</td>
                <td className={`${styles.alignRight} ${styles.kostenLight}`}>{formatBedrag(kostenQ3)}</td>
                <td className={`${styles.alignRight} ${styles.kostenLight}`}>{formatBedrag(kostenQ4)}</td>
              </tr>
            </tbody>
            <tfoot>
              {/* Separator - Morris: separator-row */}
              <tr className={styles.separatorRow}>
                <td></td>
                <td className={styles.alignRight}>========</td>
                <td colSpan={4}></td>
              </tr>

              {/* Winst - Morris: winst-row */}
              <tr className={styles.winstRow}>
                <td><strong>WINST</strong></td>
                <td className={`${styles.alignRight} ${styles.winst}`}>
                  <strong>{formatBedrag((data.omzet.kwartalen[0] + data.omzet.kwartalen[1] + data.omzet.kwartalen[2] + data.omzet.kwartalen[3]) - (kostenQ1 + kostenQ2 + kostenQ3 + kostenQ4))}</strong>
                </td>
                <td className={`${styles.alignRight} ${styles.winstLight}`}>
                  {formatBedrag(data.omzet.kwartalen[0] - kostenQ1)}
                </td>
                <td className={`${styles.alignRight} ${styles.winstLight}`}>
                  {formatBedrag(data.omzet.kwartalen[1] - kostenQ2)}
                </td>
                <td className={`${styles.alignRight} ${styles.winstLight}`}>
                  {formatBedrag(data.omzet.kwartalen[2] - kostenQ3)}
                </td>
                <td className={`${styles.alignRight} ${styles.winstLight}`}>
                  {formatBedrag(data.omzet.kwartalen[3] - kostenQ4)}
                </td>
              </tr>

              {/* Separator - Morris: separator-row */}
              <tr className={styles.separatorRow}>
                <td></td>
                <td className={styles.alignRight}>========</td>
                <td colSpan={4}></td>
              </tr>

              {/* Controle - Morris: controle-row */}
              <tr className={styles.controleRow}>
                <td>Controle (omzet)</td>
                <td className={styles.alignRight}>
                  {formatBedrag(data.omzet.kwartalen[0] + data.omzet.kwartalen[1] + data.omzet.kwartalen[2] + data.omzet.kwartalen[3])}
                </td>
                <td colSpan={4}></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </BoekhoudingLayout>
  );
};

export default Exploitatie;
